/**
 * Deal Room folder sharing, as the screens read it off a folder.
 *
 * The backend says, per folder, who owns it, the share covering it (the whole
 * team, or specific people on it) and the caller's own access (`owner` |
 * `edit` | `view`). A backend that predates sharing sends none of it, and
 * every folder then reads as the caller's own.
 */
import type {
	FolderInfo,
	FolderShareInfo,
	ShareMember,
	UserRef,
} from "../services/fileApi";

/** The caller's access; a folder without the field is their own. */
export const accessTo = (folder: FolderInfo): "owner" | "edit" | "view" =>
	folder.my_access ?? "owner";

/** May the caller add, replace or delete files here? */
export const canWriteFolder = (folder: FolderInfo): boolean =>
	accessTo(folder) !== "view";

/** Only the owner shares, stops sharing, or deletes the deal itself. */
export const isOwnFolder = (folder: FolderInfo): boolean =>
	accessTo(folder) === "owner";

/**
 * May the caller export this deal to Box? Owner only: an export copies the
 * deal's files into the caller's own Box, which a share does not extend to
 * (the backend refuses it too). Any Box export entry point must check this.
 */
export const canExportFolder = (folder: FolderInfo): boolean =>
	isOwnFolder(folder);

export const isSharedFolder = (folder: FolderInfo): boolean =>
	Boolean(folder.shared);

/** Is the share for specific people rather than the whole team? */
export const isPeopleShare = (share: FolderShareInfo | null | undefined) =>
	share?.audience === "people";

/**
 * The Deal Room list's two sections. A deal is "shared" whenever it is shared
 * — with the team or with specific people, the caller's own shared deals
 * included, so everything shared is in one place; "yours" is what only the
 * caller can see.
 */
export function splitByShared<T>(
	items: T[],
	folderOf: (item: T) => FolderInfo,
): { yours: T[]; shared: T[] } {
	const yours: T[] = [];
	const shared: T[] = [];
	for (const item of items) {
		(isSharedFolder(folderOf(item)) ? shared : yours).push(item);
	}
	return { yours, shared };
}

/** Up to two initials for an avatar: "Priya Shah" → "PS", "dan@x.com" → "D". */
export function initials(name: string | null | undefined): string {
	const words = (name ?? "")
		.replace(/@.*$/, "")
		.split(/[\s._-]+/)
		.filter(Boolean);
	if (words.length === 0) return "?";
	const letters =
		words.length === 1
			? words[0].slice(0, 1)
			: words[0][0] + words[words.length - 1][0];
	return letters.toUpperCase();
}

const plural = (count: number, word: string) =>
	`${count} ${word}${count === 1 ? "" : "s"}`;

/** "Priya Shah" → "Priya"; "dan@x.com" → "dan". */
export const firstName = (name: string | null | undefined): string =>
	(name ?? "").replace(/@.*$/, "").trim().split(/\s+/)[0] || "a teammate";

/** "Priya", "Priya and Dan", "Priya, Dan and Lee", "Priya, Dan and 2 others". */
export function listNames(names: string[]): string {
	if (names.length <= 1) return names[0] ?? "";
	if (names.length > 3) {
		return `${names.slice(0, 2).join(", ")} and ${plural(names.length - 2, "other")}`;
	}
	return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The pill on a shared deal: "Shared with team" or "Shared with 2 people". */
export function shareBadgeLabel(share: FolderShareInfo): string {
	if (!isPeopleShare(share)) return "Shared with team";
	const count = share.members?.length ?? 0;
	if (count === 0) return "Shared";
	return `Shared with ${count === 1 ? "1 person" : `${count} people`}`;
}

/** The members as the caller reads them: "you" first, then first names. */
const memberNames = (
	members: ShareMember[],
	currentUserId?: string | null,
): string[] => {
	const you = members.some((member) => member.id === currentUserId);
	const others = members
		.filter((member) => member.id !== currentUserId)
		.map((member) => firstName(member.name || member.email));
	return you ? ["you", ...others] : others;
};

/**
 * The folder header's line under a shared deal's name. Null when private.
 *
 * - Team: "Shared by Priya Shah · everyone on the team can add and open files".
 * - People, for the owner: "Shared with Priya and Dan".
 * - People, for a member: "Shared by Olivia Owner with you and Dan · you can
 *   open files".
 */
export function shareSummary(
	folder: FolderInfo,
	currentUserId?: string | null,
): string | null {
	const share = folder.shared;
	if (!share) return null;
	const by = share.shared_by;
	const sharedByMe = Boolean(by && currentUserId && by.id === currentUserId);
	const who = sharedByMe ? "you" : by?.name || "a teammate";
	if (isPeopleShare(share)) {
		const names = memberNames(share.members ?? [], currentUserId);
		if (accessTo(folder) === "owner") {
			return names.length
				? `Shared with ${listNames(names)}`
				: "Shared with specific people";
		}
		const what =
			accessTo(folder) === "edit"
				? "you can add and open files"
				: "you can open files";
		const withWhom = names.length ? ` with ${listNames(names)}` : "";
		return `Shared by ${who}${withWhom} · ${what}`;
	}
	const what =
		share.access === "edit" ? "can add and open files" : "can open files";
	return `Shared by ${who} · everyone on the team ${what}`;
}

/** A person connected to a file, and how: "Added by" or "Owner". */
export interface FilePerson {
	role: "Added by" | "Owner";
	user: UserRef;
}

/**
 * Everyone the Deal Room knows for one file: who added it, then the deal's
 * owner when that is someone else. Files in a shared deal belong to its owner,
 * so a teammate's upload names two people.
 */
export function filePeople(file: {
	added_by?: UserRef | null;
	dealOwner?: UserRef | null;
}): FilePerson[] {
	const people: FilePerson[] = [];
	if (file.added_by) people.push({ role: "Added by", user: file.added_by });
	if (file.dealOwner && file.dealOwner.id !== file.added_by?.id) {
		people.push({ role: "Owner", user: file.dealOwner });
	}
	return people;
}

/** "Added by Tom Teammate", "Owner Priya Shah". */
export const describePerson = (person: FilePerson): string =>
	`${person.role} ${person.user.name || "a teammate"}`;
